import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import {
  FaChevronLeft, FaFileLines, FaCode, FaBold, FaItalic, FaStrikethrough, FaListUl,
  FaHeading, FaTextWidth, FaUser, FaFileInvoice, FaWallet, FaHandshake,
  FaHouse
} from "react-icons/fa6";
import { Header } from "../../../../components/Header";
import { InputDefault } from "../../../../components/forms/InputDefault";
import { WhatsAppTemplateService } from "../../../../services/WhatsAppTemplateService";
import { SelectWaTemplateCategory } from "../../../../components/forms/selects/SelectWaTemplateCategory";

// --- STYLE KONSTAN ---
const CARD_STYLE = {
  background: "rgba(15, 23, 42, 0.5)",
  border: "1px solid rgba(51, 65, 85, 0.3)",
  backdropFilter: "blur(12px)",
};

// --- DATA DUMMY ---
const CATEGORIES = [
  { id: 'invoice', label: 'Invoice' },
  { id: 'tiket_gangguan', label: 'Tiket Gangguan' },
  { id: 'broadcast_gangguan', label: 'Broadcast Gangguan' },
  { id: 'broadcast_promo', label: 'Broadcast Promo' }
];

// --- DATA PARAMETER YANG SUDAH DI-GROUPING ---
const AVAILABLE_PARAMETERS = [
  {
    title: 'Perusahaan',
    icon: FaHouse,
    color: 'text-orange-400',
    items: [
      { key: '{nama_perusahaan}', desc: 'Nama Perusahaan' },
      { key: '{nama_singkatan}', desc: 'Nama Singkatan' },
      { key: '{no_telepon_perusahaan}', desc: 'No Telepon Perusahaan' },
    ]
  },
  {
    title: 'Pelanggan',
    icon: FaUser,
    color: 'text-blue-400',
    items: [
      { key: '{no_internet}', desc: 'No Internet' },
      { key: '{nama_lengkap}', desc: 'Nama Lengkap' },
      { key: '{no_telepon}', desc: 'No Telepon' },
      { key: '{paket_internet}', desc: 'Paket Internet' },
      { key: '{harga_paket_internet}', desc: 'Harga Paket' },
    ]
  },
  {
    title: 'Invoice',
    icon: FaFileInvoice,
    color: 'text-emerald-400',
    items: [
      { key: '{no_invoice}', desc: 'Nomor Invoice' },
      { key: '{periode}', desc: 'Periode Tagihan' },
      { key: '{tagihan}', desc: 'Nominal Tagihan' },
      { key: '{diskon}', desc: 'Diskon' },
      { key: '{ppn}', desc: 'PPN' },
      { key: '{total tagihan}', desc: 'Diskon' },
      { key: '{status}', desc: 'Status Pembayaran' },
    ]
  },
  {
    title: 'Pembayaran',
    icon: FaWallet,
    color: 'text-amber-400',
    items: [
      { key: '{nama_bank}', desc: 'Nama Bank' },
      { key: '{no_rekening}', desc: 'Nomor Rekening' },
      { key: '{atas_nama}', desc: 'Atas Nama' },
      { key: '{metode_pembayaran}', desc: 'Metode Bayar' },
    ]
  },
  {
    title: 'Mitra',
    icon: FaHandshake,
    color: 'text-purple-400',
    items: [
      { key: '{nama_mitra}', desc: 'Nama Mitra' },
      { key: '{no_telepon_mitra}', desc: 'No Telepon Mitra' },
    ]
  },
];

const FORMAT_SYMBOLS = {
  bold: '*',
  italic: '_',
  strike: '~',
  mono: '```'
};

export const WhatsAppTemplateCreate = () => {
  const navigate = useNavigate();
  const textareaRef = useRef(null);

  const [loading, setLoading] = useState(false);

  // State Form Data
  const [data, setData] = useState({
    category_id: 'invoice',
    name: '',
    body: '',
    primary: false,
    general: false,
  });

  // State Error
  const [error, setError] = useState({
    category_id: '',
    name: '',
    body: '',
  });

  // Hitung sisa karakter
  const charCount = data.body.length;
  const charLimit = 1000;
  const isNearLimit = charCount > 230;

  // Handler Input Standar
  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;

    let newValue = type === 'checkbox' ? checked : value;

    if (id === 'body' && typeof newValue === 'string') {
      if (newValue.length > charLimit) {
        newValue = newValue.substring(0, charLimit);
      }
    }

    setData(prev => ({
      ...prev,
      [id]: newValue,
    }));

    if (error[id]) {
      setError(prev => ({ ...prev, [id]: '' }));
    }
  };

  // Fungsi Format Teks
  const handleFormat = (type) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = data.body;
    const selectedText = text.substring(start, end);
    const symbol = FORMAT_SYMBOLS[type];

    if (!symbol) return;

    const before = text.substring(0, start);
    const after = text.substring(end, text.length);

    let newText = '';
    if (selectedText.length > 0) {
      newText = before + symbol + selectedText + symbol + after;
    } else {
      newText = before + symbol + symbol + after;
    }

    if (newText.length > charLimit) {
      newText = newText.substring(0, charLimit);
    }

    setData(prev => ({ ...prev, body: newText }));

    setTimeout(() => {
      textarea.focus();
      let newCursorPos = start + symbol.length;
      if (selectedText.length > 0) {
        newCursorPos += selectedText.length + symbol.length;
      }
      if (newCursorPos > newText.length) newCursorPos = newText.length;

      textarea.selectionStart = textarea.selectionEnd = newCursorPos;
    }, 0);
  };

  // Fungsi Insert Parameter
  const insertParameter = (param) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = data.body;

    const remainingSpace = charLimit - text.length;

    if (param.length > remainingSpace && text.length >= charLimit) {
      return;
    }

    const before = text.substring(0, start);
    const after = text.substring(end, text.length);

    let newText = before + param + after;
    if (newText.length > charLimit) {
      newText = newText.substring(0, charLimit);
    }

    setData(prev => ({ ...prev, body: newText }));

    setTimeout(() => {
      textarea.focus();
      let newPos = start + param.length;
      if (newPos > newText.length) newPos = newText.length;
      textarea.selectionStart = textarea.selectionEnd = newPos;
    }, 0);
  };

  // Validasi Form
  const validateForm = () => {
    let isValid = true;
    const newError = { category_id: '', name: '', body: '' };

    if (!data.category_id) {
      newError.category_id = "Kategori wajib dipilih";
      isValid = false;
    }

    if (!data.name.trim()) {
      newError.name = "Nama template wajib diisi";
      isValid = false;
    }

    if (!data.body.trim()) {
      newError.body = "Isi pesan wajib diisi";
      isValid = false;
    }

    setError(newError);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const payload = {
        category_id: data.category_id,
        name: data.name,
        body: data.body,
        primary: data.primary,
        general: data.general,
      };

      await WhatsAppTemplateService.create({ data: payload });
      navigate("/dashboard/whatsapp/template");
    } catch (err) {
      console.error("Error create template:", err);
      if (err.response?.data?.errors) {
        setError(prev => ({
          ...prev,
          ...err.response.data.errors
        }));
      } else if (err.response?.data?.message) {
        alert("Gagal menyimpan: " + err.response.data.message);
      } else {
        alert("Terjadi kesalahan saat menyimpan data.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Tambah Template Pesan - DANELA NET</title>
        <meta name="description" content="Form tambah template pesan WhatsApp." />
      </Helmet>

      <div className="relative">
        <Header showToggleSidebar={false}>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-700/50 rounded-full transition-all text-slate-400 hover:text-white"
            >
              <FaChevronLeft />
            </button>
            <div>
              <h2 className="text-base md:text-xl font-bold text-gray-100">Buat Template Baru</h2>
              <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">Konfigurasi Pesan WhatsApp</p>
            </div>
          </div>
        </Header>

        <main className="p-4 lg:p-6 space-y-6 relative z-10">
          <form onSubmit={handleSubmit} className="flex flex-col gap-6" id="template-form">

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

              {/* KOLOM KIRI */}
              <div className="lg:col-span-5 flex flex-col gap-6">
                {/* PARAMETER HELPER - DIUBAH MENJADI GROUPING */}
                <div className="mt-4 pt-4 border-t border-slate-700/30">
                  <div className="flex items-center gap-2 mb-4">
                    <FaCode className="text-xs text-cyan-400" />
                    <span className="text-[11px] font-bold text-slate-300 uppercase">Parameter Tersedia</span>
                    <span className="text-[10px] text-slate-500">(Klik untuk sisipkan)</span>
                  </div>

                  <div className="space-y-5">
                    {AVAILABLE_PARAMETERS.map((group, gIndex) => (
                      <div key={gIndex}>
                        {/* Header Group */}
                        <div className="flex items-center gap-2 mb-2">
                          <group.icon className={`text-sm ${group.color}`} />
                          <span className={`text-[11px] font-bold uppercase tracking-wider ${group.color}`}>
                            {group.title}
                          </span>
                          <div className="flex-1 h-px bg-slate-700/50"></div>
                        </div>

                        {/* List Button Parameter */}
                        <div className="flex flex-wrap gap-2">
                          {group.items.map((item, iIndex) => (
                            <button
                              key={`${gIndex}-${iIndex}`}
                              type="button"
                              onClick={() => insertParameter(item.key)}
                              className="group relative flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-cyan-900/20 border border-slate-700 hover:border-cyan-500/40 rounded-lg transition-all"
                            >
                              <span className="font-mono text-xs text-cyan-400 font-bold">{item.key}</span>
                              <span className="text-[10px] text-slate-500 group-hover:text-slate-300 hidden lg:inline-block transition-colors">
                                • {item.desc}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* KOLOM KANAN: INPUT PESAN */}
              <div className="lg:col-span-7 flex flex-col gap-6">
                <div className="rounded-2xl p-5 lg:p-6 h-full flex flex-col gap-3" style={CARD_STYLE}>

                  <section className="">
                    <h3 className="text-xs font-black uppercase text-slate-400 mb-5 tracking-[0.2em] flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                      <FaCode className="text-purple-500 text-[10px]" />
                      Pengaturan
                    </h3>

                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-xl border border-slate-700/30">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-slate-200">Utama (Primary)</span>
                          <span className="text-[10px] text-slate-500">Hanya satu per perusahaan</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            id="primary"
                            checked={data.primary}
                            onChange={handleChange}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                        </label>
                      </div>
                    </div>
                  </section>

                  <section className="">
                    <h3 className="text-xs font-black uppercase text-slate-400 mb-5 tracking-[0.2em] flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                      <FaFileLines className="text-blue-500 text-[10px]" />
                      Informasi Template
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-5">
                      <SelectWaTemplateCategory
                        label={"Kategori Pesan"}
                        value={data.category_id}
                        searchable={true}
                        setValue={(id) => setData((f) => ({ ...f, category_id: id }))}
                      />

                      <InputDefault
                        id="name"
                        label="Nama Template"
                        type="text"
                        placeholder="Misal: Invoice Bulanan V1"
                        value={data.name}
                        onChange={handleChange}
                        error={error.name}
                        required
                      />
                    </div>
                  </section>


                  <section>
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        Isi Pesan
                      </h3>
                      <div className="text-[10px] font-mono px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-400">
                        {charCount} / {charLimit}
                        {isNearLimit && <span className="text-amber-500 ml-1">!</span>}
                      </div>
                    </div>

                    {/* TOOLBAR */}
                    <div className="flex items-center gap-1 p-2 mb-2 bg-slate-900/50 rounded-lg border border-slate-700/50 w-fit">
                      <button type="button" onClick={() => handleFormat('bold')} className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" title="Tebal (Bold)"><FaBold className="text-sm" /></button>
                      <button type="button" onClick={() => handleFormat('italic')} className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" title="Miring (Italic)"><FaItalic className="text-sm" /></button>
                      <button type="button" onClick={() => handleFormat('strike')} className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" title="Coret (Strikethrough)"><FaStrikethrough className="text-sm" /></button>
                      <button type="button" onClick={() => handleFormat('mono')} className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" title="Monospace"><FaTextWidth className="text-sm" /></button>
                      <div className="w-px h-4 bg-slate-700 mx-1"></div>
                      <button type="button" onClick={() => insertParameter('- ')} className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" title="List Item"><FaListUl className="text-sm" /></button>
                    </div>

                    {/* TEXTAREA */}
                    <div className="relative flex-grow">
                      <textarea
                        ref={textareaRef}
                        id="body"
                        rows={10}
                        value={data.body}
                        onChange={handleChange}
                        className={`w-full h-full bg-slate-900/50 border rounded-xl px-4 py-3 text-sm text-slate-200 placeholder-slate-600 focus:outline-none resize-none transition-all select-text
                        ${error.body ? 'border-red-500/50 focus:border-red-500' : 'border-slate-600/50 focus:border-emerald-500/50'}
                      `}
                        placeholder="Ketik pesan Anda di sini..."
                        autoComplete="off"
                      ></textarea>
                      {error.body && <p className="text-[10px] text-red-400 mt-1">{error.body}</p>}
                    </div>
                  </section>
                </div>
              </div>

            </div>

            {/* BUTTON ACTION */}
            <div className="flex gap-4 pt-2 relative z-20">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-slate-300 border border-slate-600 hover:bg-slate-800 transition-all uppercase tracking-widest"
              >
                Batal
              </button>
              <button
                type="submit"
                form="template-form"
                disabled={loading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-xl font-bold text-sm uppercase tracking-widest shadow-lg shadow-emerald-900/20 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                    Menyimpan...
                  </>
                ) : (
                  "Simpan Template"
                )}
              </button>
            </div>

          </form>
        </main>
      </div>
    </>
  );
};